import DeckGL from '@deck.gl/react';
import { device } from 'constants/breakpoints';
import { useBrowser, useIsIntersecting } from 'hooks';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { AttributionControl, InteractiveMap, Layer, _MapContext as MapContext, NavigationControl, Source, } from 'react-map-gl';
import styled, { useTheme } from 'styled-components';
import { SiteType } from 'types/site.types';
import { createGeoJSONfromSiteCluster } from 'utils/geography';
import { useActiveContext } from '../ProductJourney/Context/ActiveContext';
import { useStackableContent } from '../StackedContent/StackableContentContext';
import { clusterCountLayer, clusterLayer, createUnclusteredPointLayer, } from '../VerifiedPayments/constants/layerDefinitions';
import find from 'lodash/lodash';
import uniqBy from 'lodash/uniqBy';
import BatchTracing from '../BatchTracingContainer/BatchTracing';
const StyledNavigationControl = styled(NavigationControl) `
  bottom: 40px;
  right: 16px;
`;
const StyledAttributionControl = styled(AttributionControl) `
  font-size: 10px;
  bottom: ${({ isMobile }) => (isMobile ? 30 : 5)}px;
  right: ${({ isMobile }) => (isMobile ? 4 : 19)}px;
  margin: 0;

  .mapboxgl-ctrl-attrib.mapboxgl-compact {
    margin: 0;

    &::after {
      bottom: 0;
      right: 0;
      margin: 0;
    }
  }
`;
const MapBoundary = styled.div `
  position: relative;
  height: ${({ theme }) => theme.distances.mobileMapHeight}px;
  width: 100%;
  overflow: hidden;

  @media ${device.desktop} {
    position: relative;
    height: 100%;
    top: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;

    .mapboxgl-ctrl-bottom-right {
      display: none;
    }
  }

  @media ${device.mobile} {
    .mapboxgl-map {
      .mapboxgl-ctrl-bottom-left {
        bottom: 0;
        right: 8px;
        left: auto;
      }

      .mapboxgl-ctrl-bottom-right {
        bottom: -5px;
        right: 88px;
      }
    }
  }
`;
const MapButtonContainer = styled.div `
  position: absolute;
  z-index: 1000;
  bottom: 8px;
  left: 8px;
`;
const MapContainer = styled.div `
  .mapboxgl-ctrl-bottom-right {
    margin-right: 42px;
    display: block;

    .mapboxgl-ctrl-attrib {
      display: none;
    }
  }
`;
const ClusterMap = ({ height = '100%' }) => {
    var _a, _b, _c, _d;
    const { isMobile } = useBrowser();
    const { stackedContents } = useStackableContent();
    const { activeStepActivity, activeSiteId, allRawMaterials, selectedRawMaterialId, isBatchTracingActive, batchTracingPossible, hasSelectedStepActivityOtherIngredients, isSelectedActivityTraceable, isSelectedStepTraceable, onActiveSiteChange, handleTraceMyBatchClick, handleStopBatchTracingClick, } = useActiveContext();
    const { isIntersecting, intersectionRef } = useIsIntersecting();
    const theme = useTheme();
    const unclusteredPointLayer = createUnclusteredPointLayer(theme);
    const mapRef = useRef();
    const [hasImage, setHasImage] = useState(false);
    const [cursor, setCursor] = useState('default');
    const site = activeStepActivity === null || activeStepActivity === void 0 ? void 0 : activeStepActivity.activity.site;
    const center = {
        lat: site.siteType === SiteType.CLUSTER
            ? (_a = site.centroidCoordinates) === null || _a === void 0 ? void 0 : _a.lat
            : (_b = site.locationCoordinates) === null || _b === void 0 ? void 0 : _b.lat,
        lng: site.siteType === SiteType.CLUSTER
            ? (_c = site.centroidCoordinates) === null || _c === void 0 ? void 0 : _c.lng
            : (_d = site.locationCoordinates) === null || _d === void 0 ? void 0 : _d.lng,
    };
    const [viewport, setViewport] = useState({
        latitude: center === null || center === void 0 ? void 0 : center.lat,
        longitude: center === null || center === void 0 ? void 0 : center.lng,
        zoom: 2,
        bearing: 0,
        pitch: 0,
    });
    const clusterSites = useMemo(() => {
        var _a;
        if (((_a = activeStepActivity === null || activeStepActivity === void 0 ? void 0 : activeStepActivity.activity.site) === null || _a === void 0 ? void 0 : _a.siteType) === SiteType.CLUSTER) {
            return (activeStepActivity.activity.site.sites.edges.map(({ node }) => node) ||
                []);
        }
        return [activeStepActivity.activity.site];
    }, [activeStepActivity]);
    const geojsonData = useMemo(() => createGeoJSONfromSiteCluster(clusterSites), [clusterSites]);
    const selectedRawMaterial = useMemo(() => allRawMaterials.find((rm) => rm.id === selectedRawMaterialId), [selectedRawMaterialId, allRawMaterials]);
    useEffect(() => {
        if (activeSiteId)
            return;
        setTimeout(() => {
            setViewport(Object.assign(Object.assign({}, viewport), { zoom: 7, transitionDuration: 750 }));
        }, 100);
    }, [isIntersecting, activeSiteId, stackedContents]);
    useEffect(() => {
        if (mapRef.current) {
            zoomInOnClusterSite();
        }
        else {
            setTimeout(() => {
                zoomInOnClusterSite();
            }, 500);
        }
    }, [activeSiteId, isIntersecting, stackedContents]);
    const zoomInOnClusterSite = () => {
        const map = mapRef.current;
        if (!map || !activeSiteId)
            return;
        const site = clusterSites.find(({ id }) => id === activeSiteId);
        const mapboxSource = map.getSource('clusterSites');
        // @ts-ignore
        const clusters = map.queryRenderedFeatures({ layers: ['clusters'] });
        const uniqueClusters = uniqBy(clusters, 'id');
        const setPointActive = (point) => {
            if (isNaN(point.id))
                return;
            map.setFeatureState({ source: 'clusterSites', id: point.id }, { active: point.properties.siteId === activeSiteId });
        };
        let clusterZoomTimeout;
        uniqueClusters.forEach((cluster) => {
            const { id, properties } = cluster;
            // @ts-ignore
            mapboxSource.getClusterLeaves(id, properties.point_count, 0, (_, points) => {
                const activePoint = find(points, ['properties.siteId', activeSiteId]);
                if (activePoint === null || activePoint === void 0 ? void 0 : activePoint.geometry) {
                    setViewport(Object.assign(Object.assign({}, viewport), { longitude: activePoint.geometry.coordinates[0], latitude: activePoint.geometry.coordinates[1], zoom: 12, transitionDuration: 1000 }));
                    // Timeout because the marker is only there when it's zoomed in
                    clusterZoomTimeout = setTimeout(() => {
                        if (!map || !map.querySourceFeatures)
                            return;
                        try {
                            const points = (map === null || map === void 0 ? void 0 : map.querySourceFeatures('clusterSites', {
                                sourceLayer: 'unclustered-point',
                            })) || [];
                            points.forEach(setPointActive);
                        }
                        catch (err) {
                            // Need to add this try catcher because querySourceFeatures sometimes is undefined when moving away from the page to quickly
                            console.error(err);
                        }
                    }, 1250);
                }
            });
        });
        const transitionTimeout = setTimeout(() => {
            if (!site)
                return;
            setCursor('default');
            setViewport(Object.assign(Object.assign({}, viewport), { longitude: site.locationCoordinates.lng, latitude: site.locationCoordinates.lat, zoom: 12, transitionDuration: 1000 }));
        }, 10);
        const activeTimeout = setTimeout(() => {
            var _a;
            if (!site || !mapRef.current)
                return;
            const points = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.querySourceFeatures('clusterSites', {
                sourceLayer: 'unclustered-point',
            });
            points.forEach(setPointActive);
        }, 20);
        return () => {
            clearTimeout(transitionTimeout);
            clearTimeout(activeTimeout);
            clearTimeout(clusterZoomTimeout);
        };
    };
    const onHover = useCallback((event) => {
        if (!mapRef.current)
            return;
        const map = mapRef.current;
        const points = map.queryRenderedFeatures([event.x, event.y], {
            layers: ['unclustered-point', 'clusters'],
        });
        if (points.length) {
            setCursor('pointer');
        }
        else {
            setCursor('default');
        }
    }, []);
    const onClick = useCallback((event) => {
        if (!mapRef.current)
            return;
        const map = mapRef.current;
        const points = map.queryRenderedFeatures([event.x, event.y], {
            layers: ['unclustered-point'],
        });
        if (points.length) {
            const { siteId } = points[0].properties;
            onActiveSiteChange(siteId);
            return;
        }
        const clusters = map.queryRenderedFeatures([event.x, event.y], {
            layers: ['clusters'],
        });
        if (clusters.length) {
            const cluster = clusters[0];
            const clusterId = cluster.properties.cluster_id;
            const mapboxSource = map.getSource('clusterSites');
            // @ts-ignore
            mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) {
                    return;
                }
                setViewport(Object.assign(Object.assign({}, viewport), { 
                    // @ts-ignore
                    longitude: cluster.geometry.coordinates[0], 
                    // @ts-ignore
                    latitude: cluster.geometry.coordinates[1], zoom, transitionDuration: 250 }));
            });
        }
    }, []);
    const addImages = (map) => {
        map.loadImage('https://storage.googleapis.com/seedtrace-prd.appspot.com/defaults/koa/marker-map.png', (_, image) => {
            if (!map || hasImage)
                return;
            map.addImage('map-pin', image, { sdf: true });
        });
    };
    return (React.createElement(MapBoundary, { ref: intersectionRef },
        React.createElement(MapContainer, null,
            React.createElement(DeckGL
            // @ts-ignore
            , { 
                // @ts-ignore
                ContextProvider: MapContext.Provider, initialViewState: viewport, controller: {
                    scrollZoom: false,
                    dragRotate: false,
                    doubleClickZoom: false,
                    touchZoom: true,
                    touchRotate: false,
                }, onHover: onHover, interactiveLayerIds: ['unclustered-point, clusters'], onClick: onClick, getCursor: () => cursor },
                React.createElement(StyledNavigationControl, { className: "st-desktop-only", showCompass: false }),
                React.createElement(StyledAttributionControl, { className: "st-desktop-only", isMobile: isMobile, compact: true }),
                batchTracingPossible && (React.createElement(MapButtonContainer, null,
                    React.createElement(BatchTracing, { hasOtherIngredients: hasSelectedStepActivityOtherIngredients, rawMaterial: selectedRawMaterial, batchTracingActive: isBatchTracingActive, chainStepActivity: activeStepActivity, selectedSiteId: activeSiteId, notTraceableActivity: !isSelectedActivityTraceable, notTraceableStep: !isSelectedStepTraceable, onBatchTracingStart: handleTraceMyBatchClick, onBatchTracingEnd: handleStopBatchTracingClick }))),
                React.createElement(InteractiveMap, { ref: (ref) => {
                        if (ref) {
                            const map = ref.getMap();
                            mapRef.current = map;
                            if (!hasImage) {
                                setHasImage(true);
                                addImages(map);
                            }
                        }
                    }, width: "100%", height: height, mapStyle: "mapbox://styles/seedtrace/clia0bhp5004l01pghikh13gg", mapboxApiAccessToken: process.env.NEXT_MAPBOX_API_TOKEN, mapOptions: {
                        logoPosition: isMobile ? 'bottom-left' : 'bottom-right',
                    } },
                    React.createElement(Source, { id: "clusterSites", type: "geojson", 
                        // @ts-ignore
                        data: geojsonData, cluster: true, clusterMaxZoom: 12, clusterRadius: 40, generateId: true },
                        React.createElement(Layer, Object.assign({}, clusterLayer)),
                        React.createElement(Layer, Object.assign({}, clusterCountLayer)),
                        React.createElement(Layer, Object.assign({}, unclusteredPointLayer))))))));
};
export default ClusterMap;
